import { NativeSelect } from "../componentes/NativeSelect"
import { Column } from "../componentes/TablePrincipal2"
import { BotoesDaLinha, COLUNA_DE_ACOES, TabelaDaFicha, VazioDaFicha } from "./Tabela"
import { TabelaDosItens } from "./Contrato"
import { Input } from "../componentes/Input"
import { useEffect, useState } from "react"

import {
  api,
  type ContratadoLido,
  type ContratadoNaBusca,
  type Ficha as FichaDoContrato,
  type PessoaDaEntidade,
  type ProcedenciaDoCampo,
} from "../api"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import * as Icones from "../componentes/Icones"
import { InputSelectCombobox } from "../componentes/InputSelectCombobox"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import { SITUACOES, Selo } from "../componentes/Selo"
import { ComDica } from "../componentes/ComDica"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { data, documento, moeda, rotulo, numeroComAno, mascaraDeDocumento } from "../formato"
import { BlocoEditavel, CampoEmLinha, procedenciaDe } from "./Campos"
import {
  GRADE_FORMULARIO,
  MAIUSCULA,
  MAIUSCULA_INLINE,
  RECUSA,
  ROTULO_DE_CAMPO,
  TEXTO_DO_CADASTRO,
} from "../lib/classes"
import { cn } from "../lib/cn"

const TIPOS: Array<[string, string]> = [
  ["servico_continuo", "Serviço contínuo"],
  ["servico_escopo", "Serviço por escopo"],
  ["obra", "Obra"],
  ["reforma", "Reforma"],
  ["fornecimento", "Fornecimento"],
  ["fornecimento_continuo", "Fornecimento contínuo"],
]

const MODALIDADES: Array<[string, string]> = [
  ["pregao", "Pregão"],
  ["concorrencia", "Concorrência"],
  ["concurso", "Concurso"],
  ["leilao", "Leilão"],
  ["dialogo_competitivo", "Diálogo competitivo"],
  ["dispensa", "Dispensa"],
  ["inexigibilidade", "Inexigibilidade"],
]

const PAPEIS: Array<[string, string]> = [
  ["gestor", "Gestor do contrato"],
  ["fiscal_titular", "Fiscal titular"],
  ["fiscal_substituto", "Fiscal substituto"],
]

const SO_QUEM_RESPONDE = "Só quem responde pelo contrato altera esta parte da ficha: o gestor da entidade ou o fiscal designado"

type PropsDoBloco = {
  ficha: FichaDoContrato
  aoAtualizar: (ficha: FichaDoContrato) => void
}

function comoTexto(valor: string | null): string {
  return valor ?? ""
}

type Designacao = FichaDoContrato["fiscais"][number]

export function BlocoIdentificacao({ ficha, aoAtualizar }: PropsDoBloco) {
  const [rascunho, definirRascunho] = useState(() => rascunhoDaIdentificacao(ficha))
  const proc = (campo: string): ProcedenciaDoCampo | undefined =>
    procedenciaDe(ficha.procedencia, campo)

  return (
    <BlocoEditavel
      editavel={ficha.pode_editar !== false}
      motivoSemEdicao={SO_QUEM_RESPONDE}
      falta={faltaNoFormulario([
        ["o número do contrato", rascunho.numero],
        ["o ano", String(rascunho.ano ?? "")],
      ])}
      edicao="gaveta"
      mensagemDeSucesso="Identificação salva"
      titulo="Identificação"
      recado={
        "O que identifica o ajuste no processo e no Tribunal: número, ano, " +
        "processo administrativo, tipo, modalidade e a cláusula de prorrogação."
      }
      leitura={
        <div className="flex flex-col">
          <CampoEmLinha
            rotulo="Número do contrato"
            valor={`${numeroComAno(ficha.numero, ficha.ano)}`}
            procedencia={proc("numero")}
          />
          <CampoEmLinha
            rotulo="Processo administrativo"
            valor={ficha.processo_administrativo ?? "—"}
            procedencia={proc("processo_administrativo")}
          />
          <CampoEmLinha
            rotulo="Tipo de contrato"
            valor={rotulo(ficha.tipo)}
            procedencia={proc("tipo")}
          />
          <CampoEmLinha
            rotulo="Modalidade"
            valor={
              <>
                {rotulo(ficha.modalidade)}
                {ficha.modalidade_a_confirmar && (
                  <>
                    {" "}
                    <ComDica dica={SITUACOES.modalidade_a_confirmar.dica} semSublinhado>
                      <Selo situacao="modalidade_a_confirmar" />
                    </ComDica>
                  </>
                )}
              </>
            }
            procedencia={proc("modalidade")}
          />
          <CampoEmLinha
            rotulo="Natureza do objeto"
            valor={
              ficha.natureza_do_objeto === null
                ? "—"
                : rotulo(ficha.natureza_do_objeto)
            }
            procedencia={proc("natureza_do_objeto")}
          />
          <CampoEmLinha
            rotulo="Cláusula de prorrogação"
            valor={
              ficha.prorrogavel === true
                ? "prorrogável"
                : ficha.prorrogavel === false
                  ? "sem cláusula de prorrogação"
                  : "—"
            }
            procedencia={proc("prorrogavel")}
          />
          <CampoEmLinha
            rotulo="Teto de prorrogação"
            valor={ficha.prorrogacao_limite ?? "—"}
            procedencia={proc("prorrogacao_limite")}
          />
          <CampoEmLinha
            rotulo="Fundamento legal"
            valor={ficha.fundamento_legal ?? "—"}
          />
          <CampoEmLinha
            rotulo="Regime de execução"
            valor={ficha.regime_de_execucao ?? "—"}
          />
        </div>
      }
      formulario={() => (
        <div className={GRADE_FORMULARIO}>
          <div>
            <RotuloDoCampo htmlFor="ident-numero" obrigatorio>
              Número do contrato
            </RotuloDoCampo>
            <Input
              id="ident-numero"
              required
              name="numero-do-contrato"
              autoComplete="off"
              value={rascunho.numero}
              onChange={(e) =>
                definirRascunho({ ...rascunho, numero: e.target.value })
              }
            />
          </div>
          <div>
            <RotuloDoCampo htmlFor="ident-ano" obrigatorio>
              Ano
            </RotuloDoCampo>
            <Input
              id="ident-ano"
              required
              type="number"
              name="ano-do-contrato"
              autoComplete="off"
              value={rascunho.ano}
              onChange={(e) => definirRascunho({ ...rascunho, ano: e.target.value })}
            />
          </div>
          <div className="col-span-full">
            <label className={ROTULO_DE_CAMPO} htmlFor="ident-processo">
              Processo administrativo
            </label>
            <Input
              id="ident-processo"
              name="processo-administrativo"
              autoComplete="off"
              placeholder="Número/ano, como vem do ATHUS"
              value={rascunho.processo}
              onChange={(e) =>
                definirRascunho({ ...rascunho, processo: e.target.value })
              }
            />
          </div>
          <div>
            <RotuloDoCampo htmlFor="ident-tipo" obrigatorio>
              Tipo de contrato
            </RotuloDoCampo>
            <NativeSelect
              id="ident-tipo"
              required
              name="tipo-do-contrato"
              autoComplete="off"
              value={rascunho.tipo}
              onChange={(e) => definirRascunho({ ...rascunho, tipo: e.target.value })}
            >
              {TIPOS.map(([chave, nome]) => (
                <option key={chave} value={chave}>
                  {nome}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <RotuloDoCampo htmlFor="ident-modalidade" obrigatorio>
              Modalidade
            </RotuloDoCampo>
            <NativeSelect
              id="ident-modalidade"
              required
              name="modalidade"
              autoComplete="off"
              value={rascunho.modalidade}
              onChange={(e) =>
                definirRascunho({ ...rascunho, modalidade: e.target.value })
              }
            >
              {MODALIDADES.map(([chave, nome]) => (
                <option key={chave} value={chave}>
                  {nome}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="ident-natureza">
              Natureza do objeto
            </label>
            <NativeSelect
              id="ident-natureza"
              name="natureza-do-objeto"
              autoComplete="off"
              value={rascunho.natureza}
              onChange={(e) =>
                definirRascunho({ ...rascunho, natureza: e.target.value })
              }
            >
              <option value="">Não classificada</option>
              <option value="servico">Serviço</option>
              <option value="fornecimento">Fornecimento</option>
            </NativeSelect>
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="ident-prorrogavel">
              Cláusula de prorrogação
            </label>
            <NativeSelect
              id="ident-prorrogavel"
              name="clausula-de-prorrogacao"
              autoComplete="off"
              value={rascunho.prorrogavel}
              onChange={(e) =>
                definirRascunho({ ...rascunho, prorrogavel: e.target.value })
              }
            >
              <option value="">Cláusula não lida</option>
              <option value="sim">Prorrogável</option>
              <option value="nao">Sem cláusula de prorrogação</option>
            </NativeSelect>
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="ident-teto">
              Teto de prorrogação
            </label>
            <Input
              id="ident-teto"
              name="teto-de-prorrogacao"
              autoComplete="off"
              placeholder="10 anos, igual período"
              value={rascunho.tetoDaProrrogacao}
              onChange={(e) =>
                definirRascunho({ ...rascunho, tetoDaProrrogacao: e.target.value })
              }
            />
          </div>
        </div>
      )}
      aoGravar={async () => {
        const atualizada = await api.editarIdentificacao(ficha.id, {
          numero: rascunho.numero.trim(),
          ano: Number(rascunho.ano),
          processo_administrativo:
            rascunho.processo.trim() === "" ? null : rascunho.processo.trim(),
          tipo: rascunho.tipo,
          modalidade: rascunho.modalidade,
          natureza_do_objeto: rascunho.natureza === "" ? null : rascunho.natureza,
          prorrogavel:
            rascunho.prorrogavel === ""
              ? null
              : rascunho.prorrogavel === "sim",
          prorrogacao_limite:
            rascunho.tetoDaProrrogacao.trim() === ""
              ? null
              : rascunho.tetoDaProrrogacao.trim(),
        })
        aoAtualizar(atualizada)
        definirRascunho(rascunhoDaIdentificacao(atualizada))
      }}
    />
  )
}

function rascunhoDaIdentificacao(ficha: FichaDoContrato) {
  return {
    numero: ficha.numero,
    ano: String(ficha.ano),
    processo: comoTexto(ficha.processo_administrativo),
    tipo: ficha.tipo,
    modalidade: ficha.modalidade,
    natureza: comoTexto(ficha.natureza_do_objeto),
    prorrogavel:
      ficha.prorrogavel === null ? "" : ficha.prorrogavel ? "sim" : "nao",
    tetoDaProrrogacao: comoTexto(ficha.prorrogacao_limite),
  }
}

export function BlocoObjeto({ ficha, aoAtualizar }: PropsDoBloco) {
  const [rascunho, definirRascunho] = useState(() => rascunhoDoObjeto(ficha))
  const espelhado = procedenciaDe(ficha.procedencia, "objeto")

  return (
    <BlocoEditavel
      editavel={ficha.pode_editar !== false}
      motivoSemEdicao={SO_QUEM_RESPONDE}
      falta={rascunho.objeto.trim() === "" ? "Falta o objeto" : null}
      edicao="gaveta"
      mensagemDeSucesso="Objeto salvo"
      titulo="Objeto e itens"
      recado={
        "O que foi contratado e por quanto."
      }
      leitura={
        <>
          <div className="flex flex-col">
            <CampoEmLinha
              rotulo="Objeto"
              valor={<span className={TEXTO_DO_CADASTRO}>{ficha.objeto}</span>}
              procedencia={espelhado}
              longo
            />
            <CampoEmLinha
              rotulo="Valor inicial"
              valor={moeda(ficha.valor_inicial)}
              procedencia={procedenciaDe(ficha.procedencia, "valor_inicial")}
            />
            <CampoEmLinha
              rotulo="Valor atual"
              valor={moeda(ficha.valor_atual)}
              procedencia={procedenciaDe(ficha.procedencia, "valor_atual")}
            />
          </div>
          <TabelaDosItens itens={ficha.itens} />
        </>
      }
      formulario={() => (
        <div className={GRADE_FORMULARIO}>
          <div className="col-span-full">
            <RotuloDoCampo htmlFor="objeto-texto" obrigatorio>
              Objeto
            </RotuloDoCampo>
            <Input
              id="objeto-texto"
              required
              name="objeto-do-contrato"
              autoComplete="off"
              value={rascunho.objeto}
              onChange={(e) =>
                definirRascunho({ ...rascunho, objeto: e.target.value })
              }
            />
            {espelhado?.reescrito_pela_carga === true && (
              <p className={cn("mt-1 max-w-[70ch] text-xs text-muted-foreground", MAIUSCULA)}>{espelhado.fundamento}</p>
            )}
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="objeto-inicial">
              Valor inicial
            </label>
            <Input
              id="objeto-inicial"
              name="valor-inicial"
              autoComplete="off"
              inputMode="decimal"
              placeholder="Vazio - não informado"
              value={rascunho.valorInicial}
              onChange={(e) =>
                definirRascunho({ ...rascunho, valorInicial: e.target.value })
              }
            />
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="objeto-atual">
              Valor atual
            </label>
            <Input
              id="objeto-atual"
              name="valor-atual"
              autoComplete="off"
              inputMode="decimal"
              placeholder="Inicial mais aditivos e apostilamentos"
              value={rascunho.valorAtual}
              onChange={(e) =>
                definirRascunho({ ...rascunho, valorAtual: e.target.value })
              }
            />
          </div>
        </div>
      )}
      aoGravar={async () => {
        const atualizada = await api.editarObjeto(ficha.id, {
          objeto: rascunho.objeto.trim(),
          valor_inicial: numeroOuNulo(rascunho.valorInicial),
          valor_atual: numeroOuNulo(rascunho.valorAtual),
        })
        aoAtualizar(atualizada)
        definirRascunho(rascunhoDoObjeto(atualizada))
      }}
    />
  )
}

function rascunhoDoObjeto(ficha: FichaDoContrato) {
  return {
    objeto: ficha.objeto,
    valorInicial: comoTexto(ficha.valor_inicial),
    valorAtual: comoTexto(ficha.valor_atual),
  }
}

function numeroOuNulo(texto: string): string | null {
  const limpo = texto.trim().replace(/\./g, "").replace(",", ".")
  return limpo === "" ? null : limpo
}

function ContratadoLidoNoDocumento({
  ficha,
  lido,
  aoAtualizar,
}: PropsDoBloco & { lido: ContratadoLido }) {
  const [enviando, definirEnviando] = useState(false)
  const [erro, definirErro] = useState<string | null>(null)
  const semRazao = lido.razao_social === null

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border border-(--borda) bg-accent p-3">
      <p className="text-(length:--texto-sm) font-semibold text-foreground">
        Lido no documento do contrato{lido.pagina !== null ? `, p. ${lido.pagina}` : ""}
      </p>
      <p className="text-(length:--texto-sm) text-foreground">
        <span className={TEXTO_DO_CADASTRO}>{lido.razao_social ?? "Razão social não lida"}</span> ·{" "}
        {documento(lido.documento)}
      </p>
      {lido.trecho && (
        <p className="text-(length:--texto-sm) text-muted-foreground italic">{lido.trecho}</p>
      )}
      {erro !== null && <p className="text-(length:--texto-sm) text-(--vermelho) first-letter:uppercase">{erro}</p>}
      {ficha.pode_editar !== false && (
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={enviando || semRazao}
          title={semRazao ? "Falta a razão social, que não foi lida: cadastre o contratado pelo Editar" : undefined}
          onClick={async () => {
            if (lido.razao_social === null) return
            definirEnviando(true)
            definirErro(null)
            try {
              aoAtualizar(
                await api.escolherContratado(ficha.id, {
                  documento: lido.documento,
                  razao_social: lido.razao_social,
                }),
              )
            } catch (falha) {
              definirErro(falha instanceof Error ? falha.message : "Não foi possível confirmar")
            } finally {
              definirEnviando(false)
            }
          }}
        >
          <Icones.Validar />
          Confirmar este contratado
        </Button>
      </div>
      )}
    </div>
  )
}

export function BlocoContratado({ ficha, aoAtualizar }: PropsDoBloco) {
  const [busca, definirBusca] = useState("")
  const [achados, definirAchados] = useState<ContratadoNaBusca[]>([])
  const [escolhido, definirEscolhido] = useState<number | null>(null)
  const [novo, definirNovo] = useState({ documento: "", razaoSocial: "" })
  const escolher = (candidato: { id: number; documento: string; razao_social: string }) => {
    definirEscolhido(candidato.id)
    definirNovo({ documento: mascaraDeDocumento(candidato.documento), razaoSocial: candidato.razao_social })
  }

  useEffect(() => {
    let vivo = true
    const relogio = setTimeout(() => {
      api
        .contratados(busca)
        .then((lista) => {
          if (vivo) definirAchados(lista)
        })
        .catch(() => {
          if (vivo) definirAchados([])
        })
    }, 250)
    return () => {
      vivo = false
      clearTimeout(relogio)
    }
  }, [busca])

  const naoIdentificado = /^0+$/.test(ficha.contratado.documento.replace(/\D/g, ""))

  return (
    <BlocoEditavel
      editavel={ficha.pode_editar !== false}
      motivoSemEdicao={SO_QUEM_RESPONDE}
      falta={
        escolhido === null
          ? faltaNoFormulario([
              ["o CNPJ ou CPF do contratado", novo.documento],
              ["a razão social", novo.razaoSocial],
            ])
          : null
      }
      edicao="gaveta"
      mensagemDeSucesso="Contratado salvo"
      titulo="Contratado"
      recado={
        naoIdentificado
          ? "Este contrato veio do ATHUS, que não informa quem é o " +
            "contratado — ele está no documento do contrato. Escolha o fornecedor já cadastrado " +
            "ou cadastre-o: sem ele, a regularidade do fornecedor não tem " +
            "sobre quem responder."
          : "Quem assina do outro lado. É dele que a aba Fiscalização consulta " +
            "as certidões."
      }
      leitura={
        <div className="flex flex-col">
          <CampoEmLinha
            rotulo="Razão social"
            valor={<span className={TEXTO_DO_CADASTRO}>{ficha.contratado.razao_social}</span>}
            procedencia={procedenciaDe(ficha.procedencia, "contratado_id")}
            longo
          />
          <CampoEmLinha
            rotulo="CNPJ/CPF"
            valor={documento(ficha.contratado.documento)}
          />
          {ficha.contratado_lido && (
            <ContratadoLidoNoDocumento ficha={ficha} lido={ficha.contratado_lido} aoAtualizar={aoAtualizar} />
          )}
        </div>
      }
      formulario={() => (
        <div data-slot="formulario-do-contratado" className="flex flex-col gap-2">
          <label className={ROTULO_DE_CAMPO} htmlFor="contratado-busca">
            Procurar um contratado já cadastrado
          </label>
          <Input
            id="contratado-busca"
            type="search"
            name="busca-de-contratado"
            autoComplete="off"
            data-form-type="other"
            spellCheck={false}
            placeholder="Razão social ou CNPJ"
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
          />
          <TabelaDaFicha<(typeof achados)[number]>
            dados={achados}
            vazio={
              <VazioDaFicha
                titulo="Nenhum contratado casa com essa busca nesta entidade"
                descricao="Se ele ainda não existe aqui, cadastre-o abaixo."
              />
            }
            aoClicarNaLinha={escolher}
            linhaSelecionada={(candidato) => escolhido === candidato.id}
          >
            <Column<(typeof achados)[number]>
              name="razao_social"
              label="Contratado"
              render={(_v, candidato) => <span className={TEXTO_DO_CADASTRO}>{candidato.razao_social}</span>}
            />
            <Column<(typeof achados)[number]>
              name="documento"
              label="CNPJ ou CPF"
              render={(_v, candidato) => <span className="whitespace-nowrap tabular-nums">{documento(candidato.documento)}</span>}
            />
          </TabelaDaFicha>

          <div className={GRADE_FORMULARIO}>
            <div>
              <RotuloDoCampo htmlFor="contratado-documento" obrigatorio={escolhido === null}>
                {escolhido === null ? "CNPJ ou CPF do contratado novo" : "CNPJ ou CPF do contratado escolhido"}
              </RotuloDoCampo>
              <Input
                id="contratado-documento"
                required={escolhido === null}
                name="documento-do-contratado"
                autoComplete="off"
                inputMode="numeric"
                maxLength={18}
                placeholder="CNPJ ou CPF"
                value={novo.documento}
                onChange={(e) => {
                  definirEscolhido(null)
                  definirNovo({ ...novo, documento: mascaraDeDocumento(e.target.value) })
                }}
              />
            </div>
            <div>
              <RotuloDoCampo htmlFor="contratado-razao" obrigatorio={escolhido === null}>
                Razão social
              </RotuloDoCampo>
              <Input
                id="contratado-razao"
                required={escolhido === null}
                name="razao-social-do-contratado"
                autoComplete="off"
                value={novo.razaoSocial}
                onChange={(e) => {
                  definirEscolhido(null)
                  definirNovo({ ...novo, razaoSocial: e.target.value })
                }}
              />
            </div>
          </div>
        </div>
      )}
      aoGravar={async () => {
        const atualizada =
          escolhido !== null
            ? await api.escolherContratado(ficha.id, { contratado_id: escolhido })
            : await api.escolherContratado(ficha.id, {
                documento: novo.documento,
                razao_social: novo.razaoSocial,
              })
        aoAtualizar(atualizada)
        definirEscolhido(null)
        definirNovo({ documento: "", razaoSocial: "" })
      }}
    />
  )
}

export function BlocoDesignados({
  ficha,
  aoAtualizar,
  pedidoDeEdicao,
  aoAtenderPedido,
}: PropsDoBloco & { pedidoDeEdicao?: number; aoAtenderPedido?: () => void }) {
  const [pessoas, definirPessoas] = useState<PessoaDaEntidade[]>([])
  const [erroDasPessoas, definirErroDasPessoas] = useState<string | null>(null)
  const [carregandoPessoas, definirCarregandoPessoas] = useState(true)
  const [rascunho, definirRascunho] = useState({
    usuarioId: "",
    papel: "fiscal_titular",
    ato: "",
    inicio: "",
  })

  useEffect(() => {
    let vivo = true
    api
      .pessoas()
      .then((lista) => {
        if (vivo) definirPessoas(lista)
      })
      .catch((falha: unknown) => {
        if (vivo)
          definirErroDasPessoas(
            falha instanceof Error ? falha.message : "falha ao carregar as contas",
          )
      })
      .finally(() => {
        if (vivo) definirCarregandoPessoas(false)
      })
    return () => {
      vivo = false
    }
  }, [])

  const vigentes = ficha.fiscais.filter((f) => f.vigente)
  const [encerrando, definirEncerrando] = useState<(typeof ficha.fiscais)[number] | null>(null)

  return (
    <BlocoEditavel
      editavel={ficha.pode_editar !== false}
      motivoSemEdicao={SO_QUEM_RESPONDE}
      falta={faltaNoFormulario([["o fiscal", rascunho.usuarioId]])}
      titulo="Designados"
      inclui
      edicao="gaveta"
      mensagemDeSucesso="Designação registrada"
      pedidoDeEdicao={pedidoDeEdicao}
      aoAtenderPedido={aoAtenderPedido}
      recado={
        vigentes.length === 0
          ? "Nenhum gestor ou fiscal designado. Designe alguém abaixo."
          : "Quem responde por este contrato, com o papel e o ato da " +
            "designação. A designação encerrada continua no histórico."
      }
      leitura={
        <>
          <TabelaDaFicha<Designacao>
            dados={ficha.fiscais}
            ordenar={{ inicio: (d) => d.inicio }}
            vazio={
              <VazioDaFicha
                titulo="Nenhuma designação registrada neste contrato"
                descricao="Clique em Adicionar, escolha a pessoa, o papel e informe a portaria."
              />
            }
          >
            <Column<Designacao>
              name="usuario_nome"
              label="Pessoa"
              render={(_v, d) => <span className={cn(MAIUSCULA_INLINE, "font-medium")}>{d.usuario_nome ?? "Conta removida do cadastro"}</span>}
            />
            <Column<Designacao> name="papel" label="Papel" render={(_v, d) => <Selo situacao="papel">{rotulo(d.papel)}</Selo>} />
            <Column<Designacao>
              name="ato_de_designacao"
              label="Ato"
              render={(_v, d) => <span className={MAIUSCULA_INLINE}>{d.ato_de_designacao ?? "Sem ato de designação"}</span>}
            />
            <Column<Designacao> name="inicio" label="Desde" render={(_v, d) => <span className="tabular-nums">{data(d.inicio)}</span>} />
            <Column<Designacao>
              name="fim"
              label="Até"
              render={(_v, d) => <span className="tabular-nums">{d.fim === null ? "Vigente" : data(d.fim)}</span>}
            />
            {ficha.pode_editar !== false && (
              <Column<Designacao>
                name="acoes"
                label="Ações"
                classe={COLUNA_DE_ACOES}
                render={(_v, d) => (
                  <BotoesDaLinha
                    acoes={[
                      d.vigente && { rotulo: "Encerrar designação", icone: Icones.Encerrado, perigo: true, aoClicar: () => definirEncerrando(d) },
                    ]}
                  />
                )}
              />
            )}
          </TabelaDaFicha>
          <ConfirmarComSenha
            aberto={encerrando !== null}
            titulo="Encerrar a designação"
            tom="perigo"
            resumo={
              encerrando !== null && (
                <ResumoDoAto
                  itens={[
                    ["Pessoa", encerrando.usuario_nome ?? "Conta removida do cadastro"],
                    ["Papel", rotulo(encerrando.papel)],
                    ["Ato", encerrando.ato_de_designacao ?? "Sem ato de designação"],
                    ["Desde", data(encerrando.inicio)],
                  ]}
                />
              )
            }
            rotuloDoAto="Encerrar"
            iconeDoAto={<Icones.Encerrado />}
            aoConfirmar={(senha) =>
              api.encerrarDesignacao(ficha.id, (encerrando as { id: number }).id, null, senha).then(aoAtualizar)
            }
            aoFechar={() => definirEncerrando(null)}
          />
        </>
      }
      formulario={() => (
        <div className={GRADE_FORMULARIO}>
          {erroDasPessoas !== null && <p className={cn(RECUSA, MAIUSCULA, "col-span-full")}>{erroDasPessoas}</p>}
          <div>
            <RotuloDoCampo htmlFor="designar-pessoa" obrigatorio>
              Fiscal:
            </RotuloDoCampo>
            <InputSelectCombobox
              id="designar-pessoa"
              required
              options={pessoas.map((pessoa) => ({
                value: pessoa.id,
                label: pessoa.nome,
                subLabel: pessoa.cpf === null ? pessoa.email : `${pessoa.email} · CPF ${pessoa.cpf}`,
                busca: pessoa.cpf?.replace(/\D/g, ""),
              }))}
              value={rascunho.usuarioId === "" ? null : Number(rascunho.usuarioId)}
              onChange={(valor) =>
                definirRascunho({ ...rascunho, usuarioId: valor === null ? "" : String(valor) })
              }
              placeholder="Escolha a pessoa"
              placeholderDaBusca="Buscar por nome, e-mail ou CPF"
              loading={carregandoPessoas}
            />
          </div>
          <div>
            <RotuloDoCampo htmlFor="designar-papel" obrigatorio>
              Papel
            </RotuloDoCampo>
            <NativeSelect
              id="designar-papel"
              required
              name="papel-da-designacao"
              autoComplete="off"
              value={rascunho.papel}
              onChange={(e) => definirRascunho({ ...rascunho, papel: e.target.value })}
            >
              {PAPEIS.map(([chave, nome]) => (
                <option key={chave} value={chave}>
                  {nome}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="designar-ato">
              Ato de designação
            </label>
            <Input
              id="designar-ato"
              name="ato-de-designacao"
              autoComplete="off"
              placeholder="Portaria 012/2026"
              value={rascunho.ato}
              onChange={(e) => definirRascunho({ ...rascunho, ato: e.target.value })}
            />
          </div>
          <div>
            <label className={ROTULO_DE_CAMPO} htmlFor="designar-inicio">
              Início
            </label>
            <CampoDeData
              id="designar-inicio"
              name="inicio-da-designacao"
              valor={rascunho.inicio}
              aoMudar={(valor) => definirRascunho({ ...rascunho, inicio: valor })}
            />
          </div>
        </div>
      )}
      aoGravar={async () => {
        const atualizada = await api.designar(ficha.id, {
          usuario_id: Number(rascunho.usuarioId),
          papel: rascunho.papel,
          ato_de_designacao: rascunho.ato.trim() === "" ? null : rascunho.ato.trim(),
          inicio: rascunho.inicio === "" ? null : rascunho.inicio,
        })
        aoAtualizar(atualizada)
        definirRascunho({ ...rascunho, usuarioId: "", ato: "" })
      }}
    />
  )
}
